import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import { i18n } from './i18n';
import { useOrgId } from './org';

export interface Address {
  line1: string | null;
  line2: string | null;
  city: string | null;
  poBox: string | null;
  /** PIN code (India). */
  postalCode?: string | null;
}

export interface CompanySettings {
  id: string;
  company: {
    name: string;
    nameAr?: string | null;
    legalName: string | null;
    legalNameAr?: string | null;
    email: string | null;
    phone: string | null;
    website: string | null;
    address: Address;
    emirate: string | null;
    country: string;
  };
  vat: {
    vatRegistered: boolean;
    trn: string | null;
    /** VAT registered with the TRN still to be entered (ADR-0024). */
    trnPending?: boolean;
    vatRegistrationDate: string | null;
    taxPeriod: 'monthly' | 'quarterly';
    taxPack: string;
  };
  /** India GST registration; null for UAE clients. */
  gst?: {
    gstin: string | null;
    stateCode: string;
    registrationType: string;
    registrationDate: string | null;
    taxPeriod: 'monthly' | 'quarterly';
  } | null;
  sales: {
    defaultTermsDays: number;
    invoicePrefix: string;
    invoiceMessage: string | null;
    statementMessage: string | null;
    showSku: boolean;
    emailSubject: string;
    emailMessage: string;
    emailCc: string | null;
    emailSubjectAr?: string;
    emailMessageAr?: string;
    invoiceMessageAr?: string | null;
    amountInWords?: boolean;
  };
  /** Languages (document 15). */
  language?: {
    arabicEnabled: boolean;
    defaultLanguage: 'en' | 'ar';
    printNumerals: 'latn' | 'arab';
  };
  expenses: { defaultBillTermsDays: number; showItemsTable: boolean };
  advanced: {
    fiscalYearStart: number;
    canChangeFiscalYearStart: boolean;
    baseCurrency: string;
    chartTemplate: string | null;
    timeZone: string;
    dateFormat: string;
    enableAccountNumbers: boolean;
    warnDuplicateInvoiceNumbers: boolean;
    warnDuplicateBillNumbers: boolean;
    /** Classes, locations and projects (Advanced › Categories, Projects). */
    trackClasses?: 'off' | 'per_line' | 'per_transaction';
    trackLocations?: boolean;
    locationLabel?: string;
    useProjects?: boolean;
  };
  updatedAt: string;
}

export type SettingsPatch = {
  [K in 'company' | 'vat' | 'gst' | 'sales' | 'language' | 'expenses' | 'advanced']?: Record<
    string,
    unknown
  >;
};

export function useCompanySettings() {
  const orgId = useOrgId();
  return useQuery({
    queryKey: ['company-settings', orgId],
    queryFn: () => api<CompanySettings>(`/orgs/${orgId}/company-settings`),
    enabled: Boolean(orgId),
  });
}

export function useSaveSettings() {
  const orgId = useOrgId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: SettingsPatch) =>
      api<CompanySettings>(`/orgs/${orgId}/company-settings`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: async (saved) => {
      qc.setQueryData(['company-settings', orgId], saved);
      // the client switcher and Clients list show the name, emirate and TRN
      await qc.invalidateQueries({ queryKey: ['practice', 'clients'] });
    },
  });
}

export const EMIRATES = [
  'Abu Dhabi',
  'Dubai',
  'Sharjah',
  'Ajman',
  'Umm Al Quwain',
  'Ras Al Khaimah',
  'Fujairah',
] as const;

const EMIRATE_KEY = {
  'Abu Dhabi': 'abuDhabi',
  Dubai: 'dubai',
  Sharjah: 'sharjah',
  Ajman: 'ajman',
  'Umm Al Quwain': 'ummAlQuwain',
  'Ras Al Khaimah': 'rasAlKhaimah',
  Fujairah: 'fujairah',
} as const;

/** An emirate (stored in English, an EMIRATES value) in the reader's language. */
export function emirateLabel(emirate: string | null | undefined): string {
  if (!emirate) return '';
  const key = EMIRATE_KEY[emirate as (typeof EMIRATES)[number]];
  return key ? i18n.t(`settingsPages.company.settings.emirates.${key}`) : emirate;
}

export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const MONTH_KEY = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
] as const;

/** A month's name (1 = January) in the reader's language; English is MONTHS. */
export function monthName(month: number): string {
  const key = MONTH_KEY[month - 1];
  return key ? i18n.t(`settingsPages.company.settings.months.${key}`) : '';
}

/** "Due on receipt" or "Net n" in the reader's language. */
const termLabel = (days: number): string =>
  days === 0
    ? i18n.t('settingsPages.company.settings.terms.dueOnReceipt')
    : i18n.t('settingsPages.company.settings.terms.net', { count: days });

/** QBO "Terms" list: due on receipt or net n days. `label` is read in the reader's language. */
export const TERMS: Array<{ days: number; readonly label: string }> = [
  0, 7, 15, 30, 45, 60, 90,
].map((days) => ({
  days,
  get label() {
    return termLabel(days);
  },
}));

export const termsLabel = (days: number): string =>
  TERMS.find((t) => t.days === days)?.label ?? termLabel(days);
